/**
 * FILE: packages/supabase/src/domains/ai-conversations.ts
 * PURPOSE: Read-only service for AI assistant conversation history.
 * AGENT_CONTEXT: Staff can only read their own rows via RLS. Writes happen through the hub-assistant edge function.
 */

import type { DbClient } from '@fintree/supabase/db-client'
import { defaultDbClient } from '@fintree/supabase/db-client'

export interface AiConversationMessage {
  id: string
  staff_id: string
  session_id: string
  role: 'user' | 'assistant' | 'tool'
  content: string | null
  tool_name: string | null
  tool_input: unknown
  tool_output: unknown
  created_at: string
}

export interface AiConversationSession {
  session_id: string
  updated_at: string
  preview: string
}

export async function getAssistantHistory(
  sessionId: string,
  client: DbClient = defaultDbClient
): Promise<AiConversationMessage[]> {
  const { data, error } = await client
    .from('ai_conversations')
    .select('id, staff_id, session_id, role, content, tool_name, tool_input, tool_output, created_at')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })

  if (error) throw error
  return (data ?? []) as AiConversationMessage[]
}

export async function getAssistantSessions(
  client: DbClient = defaultDbClient
): Promise<AiConversationSession[]> {
  const { data, error } = await client
    .from('ai_conversations')
    .select('session_id, role, content, created_at')
    .order('created_at', { ascending: false })

  if (error) throw error

  const bySession = new Map<string, { updated_at: string; preview: string }>()
  for (const row of data ?? []) {
    if (!bySession.has(row.session_id)) {
      bySession.set(row.session_id, {
        updated_at: row.created_at ?? '',
        preview: (row.content as string | null) ?? `${row.role} message`,
      })
    }
  }

  return Array.from(bySession.entries()).map(([session_id, meta]) => ({
    session_id,
    updated_at: meta.updated_at,
    preview: meta.preview,
  }))
}
